import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import SelectMenu from './SelectMenu';
import '../../assets/styles/Pagination.css';

const pageItems = (page, totalPages) => {
  const pages = new Set([1, totalPages, page - 1, page, page + 1]);
  const list = [...pages].filter((n) => n >= 1 && n <= totalPages).sort((a, b) => a - b);
  const items = [];
  list.forEach((n, index) => {
    if (index > 0 && n - list[index - 1] > 1) items.push(`gap-${n}`);
    items.push(n);
  });
  return items;
};

const Pagination = ({
  page,
  totalPages,
  onChange,
  total = 0,
  pageSize,
  pageSizeOptions = [],
  onPageSizeChange,
  summary,
  labels = {},
  className = ''
}) => {
  const canResize = pageSizeOptions.length > 0 && onPageSizeChange && total > Math.min(...pageSizeOptions);
  if (totalPages <= 1 && !canResize) return null;

  return (
    <nav className={`tp-pagination ${className}`.trim()} aria-label={labels.nav}>
      {summary && <p className="tp-pagination__summary">{summary}</p>}

      <div className="tp-pagination__right">
        {canResize && (
          <div className="tp-pagination__size">
            <span aria-hidden="true">{labels.show}</span>
            <SelectMenu
              className="tp-pagination__size-select"
              value={pageSize}
              onChange={onPageSizeChange}
              label={labels.perPageLabel}
              options={pageSizeOptions.map((size) => ({ value: size, label: String(size) }))}
            />
            <span aria-hidden="true">{labels.perPage}</span>
          </div>
        )}

        {totalPages > 1 && (
          <div className="tp-pagination__controls">
            <button
              type="button"
              className="tp-pagination__step"
              onClick={() => onChange(page - 1)}
              disabled={page <= 1}
              aria-label={labels.prev}
            >
              <ChevronLeft size={16} strokeWidth={2.25} aria-hidden="true" />
              <span className="tp-pagination__step-text">{labels.prev}</span>
            </button>

            <ul className="tp-pagination__pages">
              {pageItems(page, totalPages).map((item) =>
                typeof item === 'number' ? (
                  <li key={item}>
                    <button
                      type="button"
                      className={`tp-pagination__page ${item === page ? 'is-active' : ''}`.trim()}
                      onClick={() => onChange(item)}
                      aria-current={item === page ? 'page' : undefined}
                      aria-label={labels.page ? labels.page(item) : undefined}
                    >
                      {item}
                    </button>
                  </li>
                ) : (
                  <li key={item} className="tp-pagination__gap" aria-hidden="true">…</li>
                )
              )}
            </ul>

            <button
              type="button"
              className="tp-pagination__step"
              onClick={() => onChange(page + 1)}
              disabled={page >= totalPages}
              aria-label={labels.next}
            >
              <span className="tp-pagination__step-text">{labels.next}</span>
              <ChevronRight size={16} strokeWidth={2.25} aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Pagination;
export { Pagination };
